// Read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4). A position with
// "read" (not "write") on a page's key can open it and watch queues/counts,
// but every mutating control is disabled and HiddenScanInput rejects scans
// with an error tone instead of submitting them. ProtectedRoute mounts the
// provider + banner together when the resolved level for the route is "read".
import { createContext, useContext, ReactNode } from "react";
import { Lock } from "lucide-react";
import { useTranslation } from "../i18n";
import "../styles/read-only-banner.css";

const ReadOnlyContext = createContext<boolean>(false);

export const ReadOnlyProvider = ({ value, children }: { value: boolean; children: ReactNode }) => (
    <ReadOnlyContext.Provider value={value}>{children}</ReadOnlyContext.Provider>
);

/** True when the current page is in read-only mode. False (never denies)
 *  outside a <ReadOnlyProvider> — most pages, and every existing test. */
export const useReadOnly = (): boolean => useContext(ReadOnlyContext);

/** Persistent banner shown under the page header on a read-only route.
 *  Rendered once by ProtectedRoute — pages don't need to add it themselves. */
export const ReadOnlyBanner = () => {
    const { t } = useTranslation();
    return (
        <div className="read-only-banner" role="status">
            <Lock size={18} strokeWidth={2} />
            <span>{t("read_only_mode")}</span>
        </div>
    );
};
